'use client';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import AgGrid, { ClickableCell } from '../common/AgGrid';
import { useNavigate } from 'react-router-dom';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SuccessMessage from '../common/SuccessMessage';
import {
  GetJobTypes, GetAccountingStatus, SearchJobList, GetJobDetailsById,
  GetJobDetailsbyUserId, UpdateAccountingStatus
} from '../../services/accountService';
import { AisScreenLoader } from '../../utils/AisScreenLoader';
import {
  hasPermission,
  canViewFieldOnScreen,
  canEditFieldOnScreen,
  isListAgent,
  isRestrictedUser,
  canViewField
} from '../../utils/PermissionUtils';

const COLUMN_PERMISSION_MAP = {
  jobName: 'jobname',
  type: 'jobtype',
  accountingStatus: 'accountingstatus',
  cost: 'cost',
  net: 'net',
  incomePerLetter: 'incomeperletter',
  costPerLetter: 'costperletter',
  netPerLetter: 'netperletter',
  roi: 'roi',
};

const JobsModule = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rowData, setRowData] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [clients, setClients] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [accountingStatuses, setAccountingStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingJobTypes, setLoadingJobTypes] = useState(false);
  const [loadingAccountingStatus, setLoadingAccountingStatus] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [selectedJobType, setSelectedJobType] = useState('');
  const [selectedJobTypeId, setSelectedJobTypeId] = useState(null);
  const [mailDateFrom, setMailDateFrom] = useState('');
  const [mailDateTo, setMailDateTo] = useState('');
  const [jobCode, setJobCode] = useState('');
  const [mailCode, setMailCode] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [clientError, setClientError] = useState('');
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const clientDropdownRef = useRef(null);
  const [statusMessageType, setStatusMessageType] = useState('');
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);
  const menuDropdownRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [showStatusButtons, setShowStatusButtons] = useState(false);
  const [statusUpdateMessage, setStatusUpdateMessage] = useState('');
  const [originalStatuses, setOriginalStatuses] = useState({});
  const [hasStatusChanges, setHasStatusChanges] = useState(false);
  const viewOnly = isListAgent();

  const canEditAccountingStatus = !viewOnly && !isRestrictedUser();

  const showCostFields = canViewField('cost');
  const canCreateJob = !viewOnly && !isRestrictedUser();

  const [jobCodeToIdMap, setJobCodeToIdMap] = useState({});

  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  const agGridRef = useRef(null);

  const gridApiRef = useRef(null);

  const [filteredRows, setFilteredRows] = useState(null);

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const number = Number(value);
    if (isNaN(number)) return '';
    return number.toLocaleString('en-US');
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const number = Number(value);
    if (isNaN(number)) return '';
    const isNegative = number < 0;
    const absValue = Math.abs(number).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${isNegative ? '-' : ''}$${absValue}`;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const number = Number(value);
    if (isNaN(number)) return '';
    return `${number.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}%`;
  };

  const filteredClients = useMemo(() => {
    if (!clientSearchTerm.trim()) {
      return clients.filter(client => client.isActive);
    }
    const searchTermLower = clientSearchTerm.toLowerCase().trim();
    return clients.filter(client =>
      client.isActive && client.clientName.toLowerCase().includes(searchTermLower)
    );
  }, [clients, clientSearchTerm]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      if (typeof dateString === 'string' && dateString.includes('-')) {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      }
      return dateString;
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return dateString;
    }
  };

  const formatDateForAPI = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date for API:', error);
      return null;
    }
  };

 
  const formatValueForField = (field, value) => {
    if (value === null || value === undefined) return '';

    const numericFields = ['totalMailed', 'nonDonors', 'donors'];
    const currencyFields = ['income', 'cost', 'net', 'avgGift', 'incomePerLetter', 'costPerLetter', 'netPerLetter'];
    const percentFields = ['responseRate'];
    const dateFields = ['mailDate', 'lastMailDate', 'lastUpdatedDate'];

    if (numericFields.includes(field)) return formatNumber(value);
    if (currencyFields.includes(field)) return formatCurrency(value);
    if (percentFields.includes(field)) return formatPercent(value);
    if (dateFields.includes(field)) return formatDate(value);
    if (field === 'roi') {
      return (value === '' || value === null || value === undefined || isNaN(value))
        ? ''
        : Number(value).toFixed(2);
    }
    return String(value);
  };

  const FIELD_HEADER_LABELS = {
    clientName: 'Client Name',
    jobId: 'Job ID',
    jobName: 'Job Name',
    type: 'Type',
    clientId: 'Client ID',
    accountingStatus: 'Accounting Status',
    description: 'Description',
    mailDate: 'Mail Date',
    lastMailDate: 'Last Mail Date',
    lastUpdatedDate: 'Last Update',
    totalMailed: 'Total Mailed',
    nonDonors: 'Non Donors',
    donors: 'Donations',
    responseRate: 'Response Rate',
    avgGift: 'Avg Gift',
    income: 'Income',
    cost: 'Cost',
    net: 'Net',
    incomePerLetter: 'Income/Letter',
    costPerLetter: 'Cost/Letter',
    netPerLetter: 'Net/Letter',
    roi: 'ROI',
  };

  const isFieldViewable = (field) => {
    const permName = COLUMN_PERMISSION_MAP[field];
    return permName ? canViewField(permName) : true;
  };

  const getExportColumns = () => {
    const resolveField = (c) => {
      if (!c) return null;
      if (c.field) return c.field;
      if (typeof c.getColId === 'function') return c.getColId();
      if (c.colId) return c.colId;
      if (c.colDef && c.colDef.field) return c.colDef.field;
      return null;
    };

    if (agGridRef.current && typeof agGridRef.current.getVisibleColumns === 'function') {
      const visible = agGridRef.current.getVisibleColumns() || [];

      return visible
        .map((c) => {
          const field = resolveField(c);
          if (!field || field === 'action') return null;
         
          if (!isFieldViewable(field)) return null;
          const headerName =
            FIELD_HEADER_LABELS[field] ||
            c.headerName ||
            (c.colDef && c.colDef.headerName) ||
            field;
          return { field, headerName };
        })
        .filter(Boolean);
    }

    return columnDefs
      .filter((c) => c.field && c.field !== 'action' && isFieldViewable(c.field))
      .map((c) => ({ field: c.field, headerName: c.headerName || c.field }));
  };
  const getExportRows = () => {
    const api = gridApiRef.current;
    const allRows = (rowData || []).filter((r) => !r.isFooter);

   
    let visible = null;
    if (api && typeof api.forEachNodeAfterFilter === 'function') {
      const rows = [];
      api.forEachNodeAfterFilter((node) => {
        if (node.data && !node.data.isFooter) rows.push(node.data);
      });
      visible = rows;
    }

    const isFiltered = visible !== null && visible.length !== allRows.length;

   
    let selected = [];
    if (api && typeof api.getSelectedRows === 'function') {
      selected = api.getSelectedRows().filter((r) => r && !r.isFooter);
    }

    if (selected.length > 0) {
      if (visible !== null) {
        const visibleSet = new Set(visible);
        const checkedVisible = selected.filter((r) => visibleSet.has(r));
        if (checkedVisible.length > 0) {
          return { rows: checkedVisible, scope: 'selected' };
        }

      } else {
        return { rows: selected, scope: 'selected' };
      }
    }

    if (isFiltered) {
      return { rows: visible, scope: 'filtered' };
    }

    return {
      rows: visible !== null ? visible : allRows,
      scope: 'all',
    };
  };

  const computeTotalsForRows = (rows) => {
    if (!rows || rows.length === 0) {
      return {
        totalMailed: 0, nonDonors: 0, donors: 0, income: 0, cost: 0, net: 0,
        avgGift: 0, responseRate: 0, incomePerLetter: 0, costPerLetter: 0,
        netPerLetter: 0, roi: 0,
      };
    }
    let totalMailed = 0, donors = 0, nonDonors = 0, income = 0, cost = 0;
    rows.forEach((row) => {
      if (row.isFooter) return;
      totalMailed += Number(row.totalMailed) || 0;
      donors += Number(row.donors) || 0;
      nonDonors += Number(row.nonDonors) || 0;
      income += Number(row.income) || 0;
      cost += Number(row.cost) || 0;
    });
    const net = income - cost;
    return {
      totalMailed, donors, nonDonors, income, cost, net,
      responseRate: totalMailed > 0 ? (donors / totalMailed) * 100 : 0,
      avgGift: donors > 0 ? income / donors : 0,
      incomePerLetter: totalMailed > 0 ? income / totalMailed : 0,
      costPerLetter: totalMailed > 0 ? cost / totalMailed : 0,
      netPerLetter: totalMailed > 0 ? net / totalMailed : 0,
      roi: cost > 0 ? income / cost : 0,
    };
  };

  const exportToExcel = () => {
    const { rows: exportRows, scope } = getExportRows();

    if (!exportRows || exportRows.length === 0) {
      setMessage('No data to export');
      setMessageType('error');
      setShowMenuDropdown(false);
      return;
    }

    try {
      const exportColumns = getExportColumns();
      if (exportColumns.length === 0) {
        setMessage('No data to export');
        setMessageType('error');
        setShowMenuDropdown(false);
        return;
      }

      const exportData = exportRows.map((job) => {
        const row = {};
        exportColumns.forEach(({ field, headerName }) => {
          const raw = job[field];
          row[headerName] = formatValueForField(field, raw);
        });
        return row;
      });

      const totals = computeTotalsForRows(exportRows);
      const totalsRow = {};
      exportColumns.forEach(({ field, headerName }, idx) => {
        if (idx === 0) {
          totalsRow[headerName] = 'TOTAL';
        } else if (totals && field in totals) {
          totalsRow[headerName] = formatValueForField(field, totals[field]);
        } else {
          totalsRow[headerName] = '';
        }
      });
      exportData.push(totalsRow);

      const ws = XLSX.utils.json_to_sheet(exportData);

      const colWidths = [];
      Object.keys(exportData[0] || {}).forEach((key, index) => {
        const maxLength = Math.max(
          key.length,
          ...exportData.map((row) => {
            const value = row[key];
            return value ? String(value).length : 0;
          })
        );
        colWidths[index] = { wch: Math.min(maxLength + 2, 50) };
      });
      ws['!cols'] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Job Lists');

      const currentDate = new Date();
      const dateStr = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
      const fileName = `Job_Lists_${dateStr}.xlsx`;

      XLSX.writeFile(wb, fileName);

      const scopeMsg = 'Export completed successfully!';
      setMessage(scopeMsg);
      setMessageType('success');
      setShowMenuDropdown(false);
    } catch (error) {
      return error;
      setMessage('Error exporting data to Excel');
      setMessageType('error');
      setShowMenuDropdown(false);
    }
  };

  const handleMessageClose = () => {
    setMessage('');
    setMessageType('success');
  };

  const printToPDF = () => {
    const { rows: exportRows, scope } = getExportRows();

    if (!exportRows || exportRows.length === 0) {
      setMessage('No data to export');
      setMessageType('error');
      setShowMenuDropdown(false);
      return;
    }

    try {
      const exportColumns = getExportColumns();
      if (exportColumns.length === 0) {
        setMessage('No data to export');
        setMessageType('error');
        setShowMenuDropdown(false);
        return;
      }

      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4',
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const marginX = 20;
      const cardX = marginX;
      const cardY = 20;
      const cardWidth = pageWidth - marginX * 2;
      const totals = computeTotalsForRows(exportRows);

     
      const titleText =
       
        'Jobs Summary'.toUpperCase()

      // Build subtitle parts from filters
      const subtitleParts = [''];
      if (selectedJobType) subtitleParts.push(String(selectedJobType).toUpperCase());
      if (mailDateFrom || mailDateTo) {
        const monthYear = (() => {
          const d = mailDateFrom ? new Date(mailDateFrom) : (mailDateTo ? new Date(mailDateTo) : null);
          if (!d || isNaN(d.getTime())) return null;
          return d
            .toLocaleString('en-US', { month: 'long', year: 'numeric' })
            .toUpperCase();
        })();
        if (monthYear) subtitleParts.push(monthYear);
      }
      const subtitleText = subtitleParts.join(' · ');

     
      const currentDate = new Date();
      const reportDateStr = `${(currentDate.getMonth() + 1)
        .toString()
        .padStart(2, '0')}/${currentDate.getDate().toString().padStart(2, '0')}/${(currentDate.getFullYear() % 100).toString().padStart(2, '0')}`;

    
      const headerBarHeight = 60;   
      const statsBarHeight = 50;   
      const detailsBarHeight = 22;  
      const cardHeight = headerBarHeight + statsBarHeight + detailsBarHeight;

      
      const BLUE_DARK = [30, 75, 120];   
      const BLUE_MID = [46, 111, 159];   
      const BLUE_DEEP = [22, 58, 95];    

   
      doc.setFillColor(...BLUE_DARK);
      doc.rect(cardX, cardY, cardWidth, headerBarHeight, 'F');

    
      const logoW = 90;
      doc.setFillColor(...BLUE_DEEP);
      doc.rect(cardX, cardY, logoW, headerBarHeight, 'F');

     
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.text('Rockwell', cardX + logoW / 2, cardY + headerBarHeight / 2 - 2, {
        align: 'center',
      });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(180, 200, 220);
      doc.text('— SOLUTIONS —', cardX + logoW / 2, cardY + headerBarHeight / 2 + 9, {
        align: 'center',
      });

      const cardCenterX = cardX + cardWidth / 2;
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text(titleText, cardCenterX, cardY + headerBarHeight / 2 + 2, { align: 'center' });

    
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(200, 215, 230);
      doc.text(subtitleText, cardCenterX, cardY + headerBarHeight / 2 + 16, { align: 'center' });

     
      const metaX = cardX + cardWidth - 14;
      const metaCenterY = cardY + headerBarHeight / 2;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(255, 255, 255);
      doc.text('Report', metaX, metaCenterY - 2, { align: 'right' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(180, 200, 220);
      doc.text(reportDateStr, metaX, metaCenterY + 10, { align: 'right' });

     
      const statsY = cardY + headerBarHeight;
      doc.setFillColor(...BLUE_MID);
      doc.rect(cardX, statsY, cardWidth, statsBarHeight, 'F');

      const stats = [
        { label: 'MAILED', value: formatNumber(totals.totalMailed) || '0' },
        { label: 'DONORS', value: formatNumber(totals.donors) || '0' },
        { label: 'INCOME', value: formatCurrency(totals.income) || '$0.00' },
        ...(showCostFields
          ? [
            { label: 'TOTAL COST', value: formatCurrency(totals.cost) || '$0.00' },
            { label: 'NET INCOME', value: formatCurrency(totals.net) || '$0.00' },
          ]
          : []),
        { label: 'AVG GIFT', value: formatCurrency(totals.avgGift) || '$0.00' },
        ...(showCostFields
          ? [
            { label: 'COST/PIECE', value: formatCurrency(totals.costPerLetter) || '$0.00' },
            { label: 'NET/PIECE', value: formatCurrency(totals.netPerLetter) || '$0.00' },
          ]
          : []),
      ];

      const statCellW = cardWidth / stats.length;
      stats.forEach((stat, i) => {
        const cellX = cardX + i * statCellW;

      
        if (i > 0) {
          doc.setDrawColor(80, 130, 170);
          doc.setLineWidth(0.5);
          doc.line(cellX, statsY + 6, cellX, statsY + statsBarHeight - 6);
        }

       
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(200, 220, 235);
        doc.text(stat.label, cellX + statCellW / 2, statsY + 15, { align: 'center' });

       
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(255, 255, 255);
        doc.text(stat.value, cellX + statCellW / 2, statsY + 35, { align: 'center' });
      });

    
      const detailsY = statsY + statsBarHeight;
      doc.setFillColor(...BLUE_DEEP);
      doc.rect(cardX, detailsY, cardWidth, detailsBarHeight, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255);
      doc.text('', cardX + 14, detailsY + detailsBarHeight / 2 + 3);

     
      const scopeLabel =
        scope === 'selected' ? 'Selected'
          : scope === 'filtered' ? 'Filtered'
            : 'All';
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(200, 215, 230);
      doc.text(
        `${scopeLabel} Records: ${exportRows.length}  ·  Confidential`,
        cardX + cardWidth - 12,
        detailsY + detailsBarHeight / 2 + 3,
        { align: 'right' }
      );

      const tableStartY = cardY + cardHeight + 10;

      const head = [exportColumns.map((c) => c.headerName)];

      const body = exportRows.map((job) =>
        exportColumns.map(({ field }) => formatValueForField(field, job[field]))
      );

      const footerRow = exportColumns.map(({ field }, idx) => {
        if (idx === 0) return 'TOTAL';
        if (totals && field in totals) return formatValueForField(field, totals[field]);
        return '';
      });

      autoTable(doc, {
        head,
        body,
        foot: [footerRow],
        startY: tableStartY,
        margin: { left: marginX, right: marginX },
        styles: {
          fontSize: 5,
          cellPadding: 3,
          overflow: 'linebreak',
          valign: 'middle',
        },
        headStyles: {
          fillColor: [46, 111, 159],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 5,
        },
        footStyles: {
          fillColor: [240, 248, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250],
        },
        didParseCell: (data) => {
          if (data.section === 'body' || data.section === 'foot') {
            const col = exportColumns[data.column.index];
            if (col) {
              const rightAlignedFields = [
                'totalMailed', 'nonDonors', 'donors',
                'income', 'cost', 'net', 'avgGift',
                'incomePerLetter', 'costPerLetter', 'netPerLetter',
                'responseRate', 'roi',
              ];
              if (rightAlignedFields.includes(col.field)) {
                data.cell.styles.halign = 'right';
              }
            }
          }
        },
       
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;
          const pageHeight = doc.internal.pageSize.getHeight();
          doc.setFontSize(8);
          doc.setTextColor(120, 120, 120);
          doc.text(
            `Page ${pageCurrent} of ${pageCount}`,
            pageWidth - marginX,
            pageHeight - 10,
            { align: 'right' }
          );
        },
      });

      const fileNameDate = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
      doc.save(`Job_Lists_${fileNameDate}.pdf`);

      const scopeMsg = 'Export completed successfully!';
      setMessage(scopeMsg);
      setMessageType('success');
      setShowMenuDropdown(false);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
    
      setMessage('Error exporting data to PDF');
      setMessageType('error');
      setShowMenuDropdown(false);
    }
  };


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuDropdownRef.current && !menuDropdownRef.current.contains(event.target)) {
        setShowMenuDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  useEffect(() => {
    const fetchClients = async () => {
      setLoading(true);
      try {
        const clientData = await GetJobDetailsbyUserId();
        setClients(clientData);
      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, []);

  useEffect(() => {
    const fetchJobTypes = async () => {
      setLoadingJobTypes(true);
      try {
        const response = await GetJobTypes();
        if (response && response.isSuccess && Array.isArray(response.data)) {
          setJobTypes(response.data);
        } else {
          setJobTypes([]);
        }
      } catch (error) {
        console.error("Error fetching job types:", error);
        setJobTypes([]);
      } finally {
        setLoadingJobTypes(false);
      }
    };
    fetchJobTypes();
  }, []);

  useEffect(() => {
    const fetchAccountingStatuses = async () => {
    
      if (!canViewField('accountingstatus')) {
        setAccountingStatuses([]);
        return;
      }
      setLoadingAccountingStatus(true);
      try {
        const response = await GetAccountingStatus();
        if (response && response.isSuccess && Array.isArray(response.data)) {
          setAccountingStatuses(response.data);
        } else {
          setAccountingStatuses([]);
        }
      } finally {
        setLoadingAccountingStatus(false);
      }
    };
    fetchAccountingStatuses();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleGlobalKeyPress = (e) => {
      if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        if (!hasSearchCriteria()) {
          setMessage('Please enter atleast one search criteria');
          setMessageType('error');
        } else {
          handleSearch();
        }
      }
    };
    document.addEventListener('keypress', handleGlobalKeyPress);
    return () => document.removeEventListener('keypress', handleGlobalKeyPress);
  }, [selectedClientIds, mailDateFrom, mailDateTo, selectedJobTypeId, jobCode, mailCode]);

 
  useEffect(() => {
    if (location.state?.clientId && clients.length > 0) {
      const returnedClientId = location.state.clientId;

      const clientToSelect = clients.find(
        client => client.clientID === returnedClientId
      );

      if (clientToSelect) {
        setSelectedClients([clientToSelect]);
        setSelectedClientIds([clientToSelect.clientID]);
        setSelectedClient(clientToSelect.clientName);
        setClientError('');
        setClientSearchTerm('');


        setTimeout(() => {
          performAutoSearch([clientToSelect.clientID]);
        }, 100);
      }
    }
  }, [location.state?.clientId, clients]);

  const performAutoSearch = async (clientIds) => {
    if (!clientIds || clientIds.length === 0) {

      return;
    }

    setSearchLoading(true);
    try {
      const searchParams = {
        clientIds: clientIds,
        mailDateFrom: mailDateFrom ? formatDateForAPI(mailDateFrom) : null,
        mailDateTo: mailDateTo ? formatDateForAPI(mailDateTo) : null,
        jobTypeId: selectedJobTypeId || null,
        jobCode: jobCode || null,
        mailCode: mailCode || null
      };



      const response = await SearchJobList(searchParams);


      if (response && typeof response === 'object' && response.success === false) {
        setRowData([]);
        setJobCodeToIdMap({});
      } else if (Array.isArray(response)) {
        const newJobCodeMap = {};

        const transformedData = response.map(job => {
          if (job.jobCode && job.jobID) {
            newJobCodeMap[job.jobCode] = job.jobID;
          }

          if (job.jobCode && !job.jobID) {
            const numbers = job.jobCode.match(/\d+/g);
            if (numbers && numbers.length > 0) {
              newJobCodeMap[job.jobCode] = parseInt(numbers[0], 10);
            }
          }

          const transformed = {
            jobId: job.jobCode || '',
            numericJobId: job.jobID || null,
            clientName: job.clientName || '',
            jobName: job.jobName || '',
            type: job.jobType || '',
            clientId: job.clientCode ? `${job.clientCode}` : '',
            accountingStatus: job.accountingStatus || job.status || '',
            description: job.description || '',
            mailDate: formatDate(job.mailDate) || '',
          };

          const additionalFields = {
            lastMailDate: 'lastMailDate',
            lastUpdatedDate: 'lastUpdatedDate',
            totalMailed: 'totalMailed',
            donors: 'donors',
            nonDonors: 'nonDonors',
            income: 'income',
            cost: 'cost',
            net: 'net',
            avgGift: 'avgGift',
            responseRate: 'responseRate',
            incomePerLetter: 'incomePerLetter',
            costPerLetter: 'costPerLetter',
            netPerLetter: 'netPerLetter',
            roi: 'roi'
          };

          Object.entries(additionalFields).forEach(([apiField, gridField]) => {
            if (job[apiField] !== undefined) {
              if (apiField.includes('Date')) {
                transformed[gridField] = formatDate(job[apiField]);
              } else {
                transformed[gridField] = job[apiField];
              }
            }
          });

          return transformed;
        });

        setJobCodeToIdMap(newJobCodeMap);
        setRowData(transformedData);

        const originalStatusMap = {};
        transformedData.forEach((job, index) => {
          originalStatusMap[index] = job.accountingStatus;
        });
        setOriginalStatuses(originalStatusMap);
        setHasStatusChanges(false);
        setShowStatusButtons(false);
        setStatusUpdateMessage('');


      } else {
        console.error("❌ Invalid response format:", response);
        setRowData([]);
        setJobCodeToIdMap({});
      }

    } catch (error) {
      console.error("❌ Auto-Search Error:", error);
      setRowData([]);
      setJobCodeToIdMap({});
    } finally {
      setSearchLoading(false);
    }
  };

  const handleClientCheckboxChange = (client) => {
    const clientId = client.clientID;
    const clientName = client.clientName;

    setSelectedClients(prev => {
      if (prev.some(c => c.clientID === clientId)) {
        const newClients = prev.filter(c => c.clientID !== clientId);
        setSelectedClientIds(newClients.map(c => c.clientID));
        if (newClients.length === 1) setSelectedClient(newClients[0].clientName);
        else if (newClients.length > 1) setSelectedClient(`${newClients.length} clients selected`);
        else setSelectedClient('');
        setClientError('');
        return newClients;
      } else {
        const newClients = [...prev, { clientID: clientId, clientName: clientName, clientCode: client.clientCode }];
        setSelectedClientIds(newClients.map(c => c.clientID));
        if (newClients.length === 1) setSelectedClient(clientName);
        else setSelectedClient(`${newClients.length} clients selected`);
        setClientError('');
        setClientSearchTerm('');
        return newClients;
      }
    });
  };

  const handleSelectAllClients = () => {
    const activeClients = filteredClients.filter(client => client.isActive);
    setSelectedClients(prev => {
      const existingIds = new Set(prev.map(c => c.clientID));
      const newClients = [...prev];
      activeClients.forEach(client => {
        if (!existingIds.has(client.clientID)) newClients.push(client);
      });
      setSelectedClientIds(newClients.map(c => c.clientID));
      if (newClients.length === 1) setSelectedClient(newClients[0].clientName);
      else if (newClients.length > 1) setSelectedClient(`${newClients.length} clients selected`);
      else setSelectedClient('');
      return newClients;
    });
    setClientError('');
    setClientSearchTerm('');
  };

  const handleClearAllClients = () => {
    setSelectedClients([]);
    setSelectedClientIds([]);
    setSelectedClient('');
    setClientError('');
    setClientSearchTerm('');
  };

  const hasSearchCriteria = () => {
    return (
      selectedClientIds.length > 0 ||
      mailDateFrom ||
      mailDateTo ||
      selectedJobTypeId ||
      jobCode ||
      mailCode
    );
  };

  const calculateTotals = useCallback(() => {
  
    const source = (filteredRows !== null ? filteredRows : rowData) || [];
    if (source.length === 0) {
      return {
        totalMailed: 0, nonDonors: 0, donors: 0, income: 0, cost: 0, net: 0,
        avgGift: 0, responseRate: 0, incomePerLetter: 0, costPerLetter: 0,
        netPerLetter: 0, roi: 0,
      };
    }
    let totalMailed = 0, donors = 0, nonDonors = 0, income = 0, cost = 0;
    source.forEach(row => {
      if (row.isFooter) return;
      totalMailed += Number(row.totalMailed) || 0;
      donors += Number(row.donors) || 0;
      nonDonors += Number(row.nonDonors) || 0;
      income += Number(row.income) || 0;
      cost += Number(row.cost) || 0;
    });
    const net = income - cost;
    return {
      totalMailed, donors, nonDonors, income, cost, net,
      responseRate: totalMailed > 0 ? (donors / totalMailed) * 100 : 0,
      avgGift: donors > 0 ? income / donors : 0,
      incomePerLetter: totalMailed > 0 ? income / totalMailed : 0,
      costPerLetter: totalMailed > 0 ? cost / totalMailed : 0,
      netPerLetter: totalMailed > 0 ? net / totalMailed : 0,
      roi: cost > 0 ? income / cost : 0,
    };
  }, [rowData, filteredRows]);

  const totals = calculateTotals();

  useEffect(() => {
    setFilteredRows(null);
  }, [rowData]);

  const handleSearch = async () => {
    if (!hasSearchCriteria()) {
      setMessage('Please enter atleast one search criteria');
      setMessageType('error');
      return;
    }
    setSearchLoading(true);
    try {
      const searchParams = {
        clientIds: selectedClientIds.length > 0 ? selectedClientIds : [],
        mailDateFrom: mailDateFrom ? formatDateForAPI(mailDateFrom) : null,
        mailDateTo: mailDateTo ? formatDateForAPI(mailDateTo) : null,
        jobTypeId: selectedJobTypeId || null,
        jobCode: jobCode || null,
        mailCode: mailCode || null
      };

      const response = await SearchJobList(searchParams);

      if (response && typeof response === 'object' && response.success === false) {
        setRowData([]);
        setJobCodeToIdMap({});
      } else if (Array.isArray(response)) {
        const newJobCodeMap = {};
        const transformedData = response.map(job => {
          if (job.jobCode && job.jobID) newJobCodeMap[job.jobCode] = job.jobID;
          if (job.jobCode && !job.jobID) {
            const numbers = job.jobCode.match(/\d+/g);
            if (numbers && numbers.length > 0) newJobCodeMap[job.jobCode] = parseInt(numbers[0], 10);
          }
          const transformed = {
            jobId: job.jobCode || '',
            numericJobId: job.jobID || null,
            clientName: job.clientName || '',
            jobName: job.jobName || '',
            type: job.jobType || '',
            clientId: job.clientCode ? `${job.clientCode}` : '',
            accountingStatus: job.accountingStatus || job.status || '',
            description: job.description || '',
            mailDate: formatDate(job.mailDate) || '',
          };
          const additionalFields = {
            lastMailDate: 'lastMailDate',
            lastUpdatedDate: 'lastUpdatedDate',
            totalMailed: 'totalMailed',
            donors: 'donors',
            nonDonors: 'nonDonors',
            income: 'income',
            cost: 'cost',
            net: 'net',
            avgGift: 'avgGift',
            responseRate: 'responseRate',
            incomePerLetter: 'incomePerLetter',
            costPerLetter: 'costPerLetter',
            netPerLetter: 'netPerLetter',
            roi: 'roi'
          };
          Object.entries(additionalFields).forEach(([apiField, gridField]) => {
            if (job[apiField] !== undefined) {
              if (apiField.includes('Date')) transformed[gridField] = formatDate(job[apiField]);
              else transformed[gridField] = job[apiField];
            }
          });
          return transformed;
        });

        setJobCodeToIdMap(newJobCodeMap);
        setRowData(transformedData);

        const originalStatusMap = {};
        transformedData.forEach((job, index) => { originalStatusMap[index] = job.accountingStatus; });
        setOriginalStatuses(originalStatusMap);
        setHasStatusChanges(false);
        setShowStatusButtons(false);
        setStatusUpdateMessage('');
      } else {
        setRowData([]);
        setJobCodeToIdMap({});
      }
    } catch (error) {
      console.error("❌ Error:", error);
      setRowData([]);
      setJobCodeToIdMap({});
    } finally {
      setSearchLoading(false);
    }
  };

  const handleClear = () => {
    setSelectedClients([]);
    setSelectedClientIds([]);
    setSelectedClient('');
    setSelectedJobType('');
    setSelectedJobTypeId(null);
    setMailDateFrom('');
    setMailDateTo('');
    setJobCode('');
    setMailCode('');
    setRowData([]);
    setJobCodeToIdMap({});
    setShowClientDropdown(false);
    setClientError('');
    setClientSearchTerm('');
    setShowStatusButtons(false);
    setHasStatusChanges(false);
    setStatusUpdateMessage('');
  };

  const handleJobTypeChange = (e) => {
    const selectedTypeName = e.target.value;
    if (selectedTypeName === "") {
      setSelectedJobType('');
      setSelectedJobTypeId(null);
      return;
    }
    const selectedJobTypeObj = jobTypes.find(jobType => jobType.jobTypeName === selectedTypeName);
    if (selectedJobTypeObj) {
      setSelectedJobType(selectedTypeName);
      setSelectedJobTypeId(selectedJobTypeObj.jobTypeID);
    }
  };

  const findNumericJobId = (jobCode) => {
    if (jobCodeToIdMap && jobCodeToIdMap[jobCode]) return jobCodeToIdMap[jobCode];
    const numbers = jobCode.match(/\d+/g);
    if (numbers && numbers.length > 0) return parseInt(numbers[0], 10);
    return null;
  };

  const handleSaveChanges = async () => {
    try {
      const updates = [];
      rowData.forEach((job, index) => {
        if (originalStatuses[index] !== job.accountingStatus) {
          const statusObj = accountingStatuses.find(s => s.accountingStatusName === job.accountingStatus);
          if (statusObj) {
            const numericJobId = job.numericJobId || findNumericJobId(job.jobId);
            if (numericJobId) {
              updates.push({ jobId: numericJobId, accountingStatusID: statusObj.accountingStatusID });
            }
          }
        }
      });

      if (updates.length === 0) {
        setShowStatusButtons(false);
        setHasStatusChanges(false);
        return;
      }

      const response = await UpdateAccountingStatus(updates);

      if (response && response.updatedCount > 0) {
        setMessageType('success');
        setMessage('Accounting status updated successfully');
        const newOriginalStatuses = { ...originalStatuses };
        if (response.data && Array.isArray(response.data)) {
          response.data.forEach(updatedJob => {
            const jobIndex = rowData.findIndex(job => {
              const numericId = job.numericJobId || findNumericJobId(job.jobId);
              return numericId === updatedJob.jobId;
            });
            if (jobIndex !== -1) {
              newOriginalStatuses[jobIndex] = updatedJob.accountingStatusName || rowData[jobIndex].accountingStatus;
              const updatedRowData = [...rowData];
              if (updatedJob.accountingStatusName) updatedRowData[jobIndex].accountingStatus = updatedJob.accountingStatusName;
              setRowData(updatedRowData);
            }
          });
        } else {
          rowData.forEach((job, index) => { newOriginalStatuses[index] = job.accountingStatus; });
        }
        setOriginalStatuses(newOriginalStatuses);
        setHasStatusChanges(false);
        setShowStatusButtons(false);
      }
      else {
        const errorMessage = response?.message || 'Error updating status';
        setMessage(errorMessage);
        setMessageType('error');
      }
    } catch (error) {
      console.error('Error saving status updates:', error);
      const errorMessage = error?.response?.data?.message || 'Error updating status';
      setMessage(errorMessage);
      setMessageType('error');
    }
  };

  const handleCancel = () => {
    const revertedData = rowData.map((job, index) => ({
      ...job,
      accountingStatus: originalStatuses[index] || job.accountingStatus
    }));
    setRowData(revertedData);
    setHasStatusChanges(false);
    setShowStatusButtons(false);
  };

  const JobIdCellRenderer = (params) => {
    const jobId = params.value;
    const [isLoading, setIsLoading] = useState(false);
    const handleClick = async (e) => {
      if (e) e.stopPropagation();
      setIsLoading(true);
      try {
        const cleanJobId = jobId.replace(' Pending', '');
        const jobDetails = await GetJobDetailsById(cleanJobId);
        navigate(`/job-details/${cleanJobId}`, { state: { jobData: jobDetails , source: 'job-module' } });
      } catch (error) {
        console.error('Error fetching job details:', error);
        const cleanJobId = jobId.replace(' Pending', '');
        navigate(`/job-details/${cleanJobId}`);
      } finally {
        setIsLoading(false);
      }
    };
    return (
      <button
        onClick={handleClick}
        disabled={isLoading}
        style={{
          background: 'none', border: 'none',
          color: isLoading ? '#666' : '#2E6F9F',
          textDecoration: isLoading ? 'none' : 'underline',
          cursor: isLoading ? 'default' : 'pointer',
          fontSize: 'inherit', padding: 0, textAlign: 'left',
        }}
      >
        {isLoading ? 'Loading...' : jobId}
      </button>
    );
  };

  const AccountingStatusCell = (params) => {
    const [status, setStatus] = useState(params.value || '');
   
    if (!canEditAccountingStatus) {
      return (
        <div style={{ padding: '4px 8px', fontSize: '14px' }}>
          {status || ''}
        </div>
      );
    }
    const handleStatusChange = (e) => {
      e.stopPropagation();
      const newStatus = e.target.value;
      setStatus(newStatus);
      if (params.data) {
        params.data.accountingStatus = newStatus;
        const jobIndex = rowData.findIndex(job => job.jobId === params.data.jobId);
        if (jobIndex !== -1) {
          const updatedData = [...rowData];
          updatedData[jobIndex] = { ...params.data, accountingStatus: newStatus, numericJobId: params.data.numericJobId };
          setRowData(updatedData);
          const hasChanges = updatedData.some((job, index) => originalStatuses[index] !== job.accountingStatus);
          setHasStatusChanges(hasChanges);
          if (hasChanges) setShowStatusButtons(true);
        }
      }
    };
    const handleSelectClick = (e) => e.stopPropagation();
    const currentStatusExists = accountingStatuses.some(s => s.accountingStatusName === status);
    return (
      <div onClick={handleSelectClick}>
        <select
          value={currentStatusExists ? status : ''}
          onChange={handleStatusChange}
          onClick={handleSelectClick}
          style={{
            width: '100%', padding: '4px 8px', border: '1px solid #ddd',
            borderRadius: '4px', backgroundColor: '#fff', fontSize: '14px', cursor: 'pointer'
          }}
        >
          {accountingStatuses.length > 0 ? (
            accountingStatuses.map((statusItem) => (
              <option key={statusItem.accountingStatusID} value={statusItem.accountingStatusName}>
                {statusItem.accountingStatusName}
              </option>
            ))
          ) : (
            <option value="">No status available</option>
          )}
        </select>
      </div>
    );
  };

  const DescriptionCellRenderer = (params) => {
    const description = params.value || '';
    const cellRef = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);
    useEffect(() => {
      const handleMouseEnter = () => { if (description.length > 35) setShowTooltip(true); };
      const handleMouseLeave = () => setShowTooltip(false);
      const cellElement = cellRef.current;
      if (cellElement) {
        cellElement.addEventListener('mouseenter', handleMouseEnter);
        cellElement.addEventListener('mouseleave', handleMouseLeave);
      }
      return () => {
        if (cellElement) {
          cellElement.removeEventListener('mouseenter', handleMouseEnter);
          cellElement.removeEventListener('mouseleave', handleMouseLeave);
        }
      };
    }, [description]);
    const truncatedText = description.length > 35 ? description.substring(0, 35) + '...' : description;
    return (
      <div ref={cellRef} className="description-cell"
        style={{ position: 'relative', height: '100%', width: '100%', display: 'flex', alignItems: 'center' }}>
        <div style={{
          whiteSpace: 'normal', wordBreak: 'break-word',
          cursor: description.length > 35 ? 'pointer' : 'default', flex: 1
        }}>
          {truncatedText}
        </div>
        {showTooltip && description.length > 35 && (
          <div className="custom-tooltip">{description}</div>
        )}
      </div>
    );
  };

  const FooterCellRenderer = (params) => {
    const columnField = params.colDef.field;
    let totalValue = '';
    let formattedValue = '';
    if (totals && columnField in totals) {
      totalValue = totals[columnField];
      switch (columnField) {
        case 'totalMailed':
        case 'nonDonors':
        case 'donors':
          formattedValue = formatNumber(totalValue); break;
        case 'income':
        case 'cost':
        case 'net':
        case 'avgGift':
        case 'incomePerLetter':
        case 'costPerLetter':
        case 'netPerLetter':
          formattedValue = formatCurrency(totalValue); break;
        case 'responseRate':
          formattedValue = formatPercent(totalValue);
          break;
        default:
          formattedValue = typeof totalValue === 'number'
            ? totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : '';
      }
    }
    return (
      <div style={{
        fontWeight: 'bold', color: '#000', padding: '8px 4px',
        textAlign: 'center', height: '100%', width: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        {formattedValue || ''}
      </div>
    );
  };



  const ViewPackageButtonRenderer = (params) => {
    const navigate = useNavigate();

    const handleClick = () => {
      const jobId = params.data?.jobId;

      if (!jobId) return;

      navigate(`/job-details/${jobId}`, {
        state: {
          activeTab: 'files'   
        }
      });
    };

    return (
      <button className="view-package-btn" onClick={handleClick}>
        View Package
      </button>
    );
  };




  useEffect(() => {
    if (location.state?.jobId) {
      const jobIdFromNavigation = location.state.jobId;

      setJobCode(jobIdFromNavigation);   


    }
  }, [location.state?.jobId]);

  useEffect(() => {
    if (jobCode) {


      handleSearch();  
    }
  }, [jobCode]);






  const columnDefs = useMemo(() => {
    const defs = [
     
      {
        headerName: 'Client Name', field: 'clientName', width: 250, maxWidth: 250, minWidth: 250,
        cellStyle: { whiteSpace: 'normal', wordBreak: 'break-word', fontWeight: 'normal' },
        autoHeight: true, wrapText: true, filter: 'agTextColumnFilter', headerClass: 'ag-header-cell-footer'
      },
      {
        headerName: 'Job ID', field: 'jobId', width: 120, maxWidth: 120, minWidth: 120,
        cellRenderer: JobIdCellRenderer, filter: 'agTextColumnFilter', cellStyle: { fontWeight: 'normal' }
      },
      {
        headerName: 'Job Name', field: 'jobName', width: 250, maxWidth: 250, minWidth: 250,
        cellStyle: { whiteSpace: 'normal', wordBreak: 'break-word', fontWeight: 'normal' },
        autoHeight: true, wrapText: true, filter: 'agTextColumnFilter', tooltipField: 'jobName'
      },
      {
        headerName: 'Type', field: 'type', width: 140, maxWidth: 140, minWidth: 140,
        filter: 'agTextColumnFilter', cellStyle: { fontWeight: 'normal' }
      },
      {
        headerName: 'Client ID', field: 'clientId', width: 160, maxWidth: 160, minWidth: 160,
        filter: 'agTextColumnFilter', cellStyle: { fontWeight: 'normal' }
      },
      {
        headerName: 'Accounting Status', field: 'accountingStatus', width: 200, maxWidth: 200, minWidth: 200,
        filter: 'agTextColumnFilter', editable: false, suppressNavigable: true,
        cellRenderer: (params) => {
          if (params.data && params.data.isFooter) return null;
          return <AccountingStatusCell {...params} />;
        },
        cellStyle: { fontWeight: 'normal' }
      },
      {
        headerName: 'Description', field: 'description', minWidth: 200, flex: 2,
        filter: 'agTextColumnFilter',
        cellStyle: { whiteSpace: 'normal', wordBreak: 'break-word', fontWeight: 'normal' },
        autoHeight: true, wrapText: true, cellRenderer: DescriptionCellRenderer, tooltipField: 'description'
      },
      {
        headerName: 'Mail Date', field: 'mailDate', width: 180, maxWidth: 180, minWidth: 180,
          comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
        filter: 'agTextColumnFilter', valueFormatter: (params) => formatDate(params.value),
        cellStyle: { fontWeight: 'normal' }
      },
      {
        headerName: 'Last Mail Date', field: 'lastMailDate', width: 220, maxWidth: 220, minWidth: 220,
          comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
        filter: 'agTextColumnFilter', valueFormatter: (params) => formatDate(params.value),
        cellStyle: { fontWeight: 'normal' }
      },
      {
        headerName: 'Last Update', field: 'lastUpdatedDate', width: 220, maxWidth: 220, minWidth: 220,
          comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
        filter: 'agTextColumnFilter', valueFormatter: (params) => formatDate(params.value),
        cellStyle: { fontWeight: 'normal' }
      },
      {
        headerName: 'Total Mailed', field: 'totalMailed', width: 220, maxWidth: 220, minWidth: 220,
        filter: 'agTextColumnFilter', valueFormatter: (params) => formatNumber(params.value),
        cellStyle: (params) => {
          const isFooter = params.node.footer || params.node.rowPinned === 'bottom';
          return { textAlign: 'right', fontWeight: isFooter ? 'bold' : 'normal' };
        },
        cellRendererSelector: (params) => (params.node.footer || params.node.rowPinned === 'bottom') ? { component: FooterCellRenderer } : undefined
      },
      {
        headerName: 'Non Donors', field: 'nonDonors', width: 180, maxWidth: 180, minWidth: 180,
        filter: 'agTextColumnFilter', valueFormatter: (params) => formatNumber(params.value),
        cellStyle: (params) => {
          const isFooter = params.node.footer || params.node.rowPinned === 'bottom';
          return { textAlign: 'right', fontWeight: isFooter ? 'bold' : 'normal' };
        },
        cellRendererSelector: (params) => (params.node.footer || params.node.rowPinned === 'bottom') ? { component: FooterCellRenderer } : undefined
      },
      {
        headerName: 'Donations', field: 'donors', width: 150, maxWidth: 150, minWidth: 150,
        filter: 'agTextColumnFilter', valueFormatter: (params) => formatNumber(params.value),
        cellStyle: (params) => {
          const isFooter = params.node.footer || params.node.rowPinned === 'bottom';
          return { textAlign: 'right', fontWeight: isFooter ? 'bold' : 'normal' };
        },
        cellRendererSelector: (params) => (params.node.footer || params.node.rowPinned === 'bottom') ? { component: FooterCellRenderer } : undefined
      },
      {
        headerName: 'Response Rate', field: 'responseRate', width: 200, maxWidth: 200, minWidth: 200,
        filter: 'agTextColumnFilter', valueFormatter: (params) => formatPercent(params.value),
        cellStyle: (params) => {
          const isFooter = params.node.footer || params.node.rowPinned === 'bottom';
          return { textAlign: 'right', fontWeight: isFooter ? 'bold' : 'normal' };
        },
        cellRendererSelector: (params) => (params.node.footer || params.node.rowPinned === 'bottom') ? { component: FooterCellRenderer } : undefined
      },
      {
        headerName: 'Avg Gift', field: 'avgGift', width: 200, maxWidth: 200, minWidth: 200,
        filter: 'agTextColumnFilter', valueFormatter: (params) => formatCurrency(params.value),
        cellStyle: (params) => {
          const isFooter = params.node.footer || params.node.rowPinned === 'bottom';
          return { textAlign: 'right', fontWeight: isFooter ? 'bold' : 'normal' };
        },
        cellRendererSelector: (params) => (params.node.footer || params.node.rowPinned === 'bottom') ? { component: FooterCellRenderer } : undefined
      },
      {
        headerName: 'Income', field: 'income', width: 200, maxWidth: 200, minWidth: 200,
        filter: 'agTextColumnFilter', valueFormatter: (params) => formatCurrency(params.value),
        cellStyle: (params) => {
          const isFooter = params.node.footer || params.node.rowPinned === 'bottom';
          return { textAlign: 'right', fontWeight: isFooter ? 'bold' : 'normal' };
        },
        cellRendererSelector: (params) => (params.node.footer || params.node.rowPinned === 'bottom') ? { component: FooterCellRenderer } : undefined
      },
      {
        headerName: 'Cost', field: 'cost', width: 180, maxWidth: 180, minWidth: 180,
        filter: 'agTextColumnFilter', valueFormatter: (params) => formatCurrency(params.value),
        cellStyle: (params) => {
          const isFooter = params.node.footer || params.node.rowPinned === 'bottom';
          return { textAlign: 'right', fontWeight: isFooter ? 'bold' : 'normal' };
        },
        cellRendererSelector: (params) => (params.node.footer || params.node.rowPinned === 'bottom') ? { component: FooterCellRenderer } : undefined
      },
      {
        headerName: 'Net', field: 'net', width: 120, maxWidth: 120, minWidth: 120,
        filter: 'agTextColumnFilter', valueFormatter: (params) => formatCurrency(params.value),
        cellStyle: (params) => {
          const isFooter = params.node.footer || params.node.rowPinned === 'bottom';
          return { textAlign: 'right', fontWeight: isFooter ? 'bold' : 'normal' };
        },
        cellRendererSelector: (params) => (params.node.footer || params.node.rowPinned === 'bottom') ? { component: FooterCellRenderer } : undefined
      },
      {
        headerName: 'Income/Letter', field: 'incomePerLetter', width: 200, maxWidth: 200, minWidth: 200,
        filter: 'agTextColumnFilter', valueFormatter: (params) => formatCurrency(params.value),
        cellStyle: (params) => {
          const isFooter = params.node.footer || params.node.rowPinned === 'bottom';
          return { textAlign: 'right', fontWeight: isFooter ? 'bold' : 'normal' };
        },
        cellRendererSelector: (params) => (params.node.footer || params.node.rowPinned === 'bottom') ? { component: FooterCellRenderer } : undefined
      },
      {
        headerName: 'Cost/Letter', field: 'costPerLetter', width: 180, maxWidth: 180, minWidth: 180,
        filter: 'agTextColumnFilter', valueFormatter: (params) => formatCurrency(params.value),
        cellStyle: (params) => {
          const isFooter = params.node.footer || params.node.rowPinned === 'bottom';
          return { textAlign: 'right', fontWeight: isFooter ? 'bold' : 'normal' };
        },
        cellRendererSelector: (params) => (params.node.footer || params.node.rowPinned === 'bottom') ? { component: FooterCellRenderer } : undefined
      },
      {
        headerName: 'Net/Letter', field: 'netPerLetter', width: 180, maxWidth: 180, minWidth: 180,
        filter: 'agTextColumnFilter', valueFormatter: (params) => formatCurrency(params.value),
        cellStyle: (params) => {
          const isFooter = params.node.footer || params.node.rowPinned === 'bottom';
          return { textAlign: 'right', fontWeight: isFooter ? 'bold' : 'normal' };
        },
        cellRendererSelector: (params) => (params.node.footer || params.node.rowPinned === 'bottom') ? { component: FooterCellRenderer } : undefined
      },
      {
        headerName: 'ROI', field: 'roi', width: 120, maxWidth: 120, minWidth: 120,
        filter: 'agTextColumnFilter', valueFormatter: params => params.value,
        cellStyle: (params) => {
          const isFooter = params.node.footer || params.node.rowPinned === 'bottom';
          return { textAlign: 'right', fontWeight: isFooter ? 'bold' : 'normal' };
        },
        cellRendererSelector: (params) => (params.node.footer || params.node.rowPinned === 'bottom') ? { component: FooterCellRenderer } : undefined
      },
      {
        field: 'action', headerName: 'Action', width: 220, maxWidth: 220, minWidth: 220,
        filter: false, sortable: false,
        cellRenderer: (params) => {
          if (params.data && params.data.isFooter) return null;
          return <ViewPackageButtonRenderer {...params} />;
        },
        cellStyle: { fontWeight: 'normal' }
      }
    ];
    return defs.filter((col) => {
      if (col.field === 'action' && (viewOnly || isRestrictedUser())) return false;
      const permName = COLUMN_PERMISSION_MAP[col.field];
      return permName ? canViewField(permName) : true;
    });
  }, [accountingStatuses, totals, rowData, originalStatuses, viewOnly]);

  const getRowDataWithFooter = useMemo(() => {
    if (!rowData || rowData.length === 0) return [];
    const footerRow = {
      isFooter: true,
      clientName: '', jobId: '', jobName: '', type: '', clientId: '', accountingStatus: '',
      description: <span style={{ fontWeight: "bold" }}>TOTAL</span>,
      mailDate: '', lastMailDate: '', lastUpdatedDate: '',
      totalMailed: totals.totalMailed, nonDonors: totals.nonDonors, donors: totals.donors,
      income: totals.income,
      // ✅ RBAC: blank cost-side totals for restricted users (columns are
      //    removed anyway; this keeps the pinned-row data clean too)
      cost: showCostFields ? totals.cost : '',
      net: showCostFields ? totals.net : '',
      avgGift: totals.avgGift, responseRate: totals.responseRate,
      incomePerLetter: canViewField('incomeperletter') ? totals.incomePerLetter : '',
      costPerLetter: showCostFields ? totals.costPerLetter : '',
      netPerLetter: showCostFields ? totals.netPerLetter : '',
      roi: showCostFields ? totals.roi : '',
    };
    return [footerRow];
  }, [rowData, totals, showCostFields]);

  const toggleFilters = () => setShowFilters(!showFilters);
  const handleCreateNewJob = () => navigate('/job-details/new');
  const toggleMenuDropdown = () => setShowMenuDropdown(!showMenuDropdown);

 
  const handleFilterChanged = useCallback((params) => {
    const api = params?.api || gridApiRef.current;
    if (!api || typeof api.forEachNodeAfterFilter !== 'function') return;

    const allCount = (rowData || []).filter((r) => !r.isFooter).length;
    const rows = [];
    api.forEachNodeAfterFilter((node) => {
      if (node.data && !node.data.isFooter) {
        rows.push(node.data);
      }
    });

    if (rows.length < allCount) {
      setFilteredRows(rows);
    } else {
      setFilteredRows(null);
    }
  }, [rowData]);

  return (

    <>

      <SuccessMessage
        message={message}
        onClose={handleMessageClose}
        type={messageType}
      />


      <div className='job-list-module'>
        <div className="job-list-container">
          <h1 className="job-list-header">Job Lists</h1>

          <div className='filter-container'>
            <div className="filter-header" style={{
              display: 'flex', justifyContent: 'space-between',
              padding: '0px 10px 0px 0px', alignItems: 'center',
              backgroundColor: '#2E6F9F', borderRadius: '8px 8px 0px 0px'
            }}>
              <h2 className='filter-heading' style={{ margin: 0, color: '#fff' }}>Filters</h2>
              <button
                onClick={toggleFilters}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '12px', color: '#fff', padding: '2px 6px',
                  transition: 'transform 0.2s ease',
                  transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
                aria-label={showFilters ? 'Hide filters' : 'Show filters'}
              >
                <img src="../assets/upward-arrow-icon.png"
                  alt={showFilters ? 'Hide filters' : 'Show filters'}
                  style={{ width: '12px', height: '12px', transition: 'transform 0.2s ease' }} />
              </button>
            </div>

            {showFilters && (
              <>
                <div className='d-flex flex-wrap gap-4 mb-3 pt-4 px-4'>
                  <div className='filter-client' style={{ width: '100%' }}>
                    <label className='filter-label'>Client(s) Selected:</label>
                    <div className='filter-input-group' ref={clientDropdownRef} style={{ position: 'relative' }}>
                      <div
                        style={{
                          minHeight: "30px", width: '100%', minHeight: '38px',
                          border: `1px solid ${clientError ? '#dc3545' : '#ccc'}`,
                          borderRadius: '4px', padding: '4px 8px', cursor: 'text',
                          backgroundColor: '#fff', display: 'flex', alignItems: 'center',
                          flexWrap: 'wrap', gap: '4px', boxSizing: 'border-box'
                        }}
                        onClick={() => {
                          setShowClientDropdown(true);
                          document.getElementById('client-search-input')?.focus();
                        }}
                      >
                        {selectedClients.length > 1 ? (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center',
                            backgroundColor: '#e9ecef', padding: '2px 6px',
                            borderRadius: '3px', fontSize: '13px', gap: '4px'
                          }}>
                            {selectedClients.length} clients selected
                            <span onClick={(e) => { e.stopPropagation(); handleClearAllClients(); }}
                              style={{ cursor: 'pointer', fontSize: '14px', color: '#666', marginLeft: '2px' }}>×</span>
                          </span>
                        ) : (
                          selectedClients.map((client) => (
                            <span key={client.clientID} style={{
                              display: 'inline-flex', alignItems: 'center',
                              backgroundColor: '#e9ecef', padding: '2px 6px',
                              borderRadius: '3px', fontSize: '13px', gap: '4px'
                            }}>
                              {client.clientName}
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newClients = selectedClients.filter(c => c.clientID !== client.clientID);
                                  setSelectedClients(newClients);
                                  setSelectedClientIds(newClients.map(c => c.clientID));
                                  if (newClients.length === 1) setSelectedClient(newClients[0].clientName);
                                  else if (newClients.length > 1) setSelectedClient(`${newClients.length} clients selected`);
                                  else setSelectedClient('');
                                }}
                                style={{ cursor: 'pointer', fontSize: '14px', color: '#666', marginLeft: '2px' }}
                              >×</span>
                            </span>
                          ))
                        )}

                        <input
                          id="client-search-input" type="text"
                          placeholder={selectedClients.length === 0 ? "Select clients..." : ""}
                          value={clientSearchTerm}
                          onChange={(e) => { setClientSearchTerm(e.target.value); setShowClientDropdown(true); }}
                          onFocus={() => setShowClientDropdown(true)}
                          onKeyDown={(e) => {
                            if (e.key === 'Backspace' && clientSearchTerm === '' && selectedClients.length > 0) {
                              const newClients = selectedClients.slice(0, -1);
                              setSelectedClients(newClients);
                              setSelectedClientIds(newClients.map(c => c.clientID));
                              if (newClients.length === 1) setSelectedClient(newClients[0].clientName);
                              else if (newClients.length > 1) setSelectedClient(`${newClients.length} clients selected`);
                              else setSelectedClient('');
                            }
                          }}
                          style={{
                            flex: 1, minWidth: '120px', border: 'none', outline: 'none',
                            padding: '6px 0', fontSize: '14px', background: 'transparent'
                          }}
                        />
                      </div>

                      {clientError && (
                        <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px', paddingLeft: '4px' }}>
                          {clientError}
                        </div>
                      )}

                      {showClientDropdown && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, right: 0,
                          backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)', zIndex: 1000,
                          maxHeight: '300px', overflow: 'hidden', marginTop: '4px',
                          display: 'flex', flexDirection: 'column'
                        }}>
                          <div style={{
                            display: 'flex', justifyContent: 'space-between',
                            padding: '8px 12px', borderBottom: '1px solid #eee', backgroundColor: '#f8f9fa'
                          }}>
                            <button onClick={handleSelectAllClients}
                              style={{
                                background: 'none', border: '1px solid #2E6F9F', color: '#2E6F9F',
                                padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                              }}>Select All</button>
                            <button onClick={handleClearAllClients}
                              style={{
                                background: 'none', border: '1px solid #dc3545', color: '#dc3545',
                                padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                              }}>Clear All</button>
                          </div>

                          <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                            {loading ? (
                              <AisScreenLoader />
                            ) : filteredClients.length > 0 ? (
                              filteredClients.map((client) => (
                                <div key={client.clientID}
                                  onClick={() => handleClientCheckboxChange(client)}
                                  style={{
                                    padding: '8px 12px', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center',
                                    borderBottom: '1px solid #f0f0f0',
                                    backgroundColor: selectedClients.some(c => c.clientID === client.clientID) ? '#e6f7ff' : '#fff',
                                    transition: 'background-color 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!selectedClients.some(c => c.clientID === client.clientID))
                                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!selectedClients.some(c => c.clientID === client.clientID))
                                      e.currentTarget.style.backgroundColor = '#fff';
                                  }}>
                                  <input type="checkbox"
                                    checked={selectedClients.some(c => c.clientID === client.clientID)}
                                    onChange={() => handleClientCheckboxChange(client)}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ marginRight: '8px', cursor: 'pointer' }} />
                                  <span>{client.clientName}</span>
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                                No clients found matching "{clientSearchTerm}"
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className='d-flex flex-wrap gap-4 mb-3 pt-2 px-4'>
                  <div className='mail-date-filter'>
                    <label className='mail-date-filter-label'>Mail Date</label>
                    <div className='mail-date-input-container'>
                      <div className="date-input-wrapper">
                        <input type="date" className={`mail-date-input ${!mailDateFrom ? 'empty' : ''}`}
                          value={mailDateFrom} onChange={(e) => setMailDateFrom(e.target.value)}
                          onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}
                          onKeyPress={handleKeyPress} />
                      </div>
                      <span className='arrow-icon'>→</span>
                      <div className="date-input-wrapper">
                        <input type="date" className={`mail-date-input ${!mailDateTo ? 'empty' : ''}`}
                          value={mailDateTo} onChange={(e) => setMailDateTo(e.target.value)}
                          onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}
                          onKeyPress={handleKeyPress} />
                      </div>
                    </div>
                  </div>

                  <div className="job-type-container">
                    <label className='job-type-label'>Job Type</label>
                    <select className='job-type-select' value={selectedJobType}
                      onChange={handleJobTypeChange} disabled={loadingJobTypes}
                      onKeyPress={handleKeyPress}>
                      <option value="">Select</option>
                      {loadingJobTypes ? (
                        <option value="">Loading job types...</option>
                      ) : jobTypes.length > 0 ? (
                        jobTypes.map((jobType) => (
                          <option key={jobType.jobTypeID} value={jobType.jobTypeName}>
                            {jobType.jobTypeName}
                          </option>
                        ))
                      ) : (
                        <option value="">No job types available</option>
                      )}
                    </select>
                  </div>

                  <div className="jobid-container">
                    <label className="jobid-label">Job ID</label>
                    <input type="text" className="jobid-input" value={jobCode}
                      onChange={(e) => setJobCode(e.target.value)} onKeyPress={handleKeyPress} />
                  </div>

                  <div className="mailcode-container">
                    <label className="mailcode-label">Mail Code</label>
                    <input type="text" className="mailcode-input" value={mailCode}
                      onChange={(e) => setMailCode(e.target.value)} onKeyPress={handleKeyPress} />
                  </div>
                </div>

                <div className="button-container">
                  <div className="button-wrapper">
                    <button className="button-search" onClick={handleSearch} disabled={searchLoading}>
                      {searchLoading ? 'Searching...' : 'Search'}
                    </button>
                  </div>
                  <div className="button-wrapper">
                    <button className="button-clear" onClick={handleClear}>Clear</button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="createnew-job-button-container d-flex justify-content-between align-items-center">
           
            {canCreateJob ? (
              <button className="createnew-job-button" onClick={handleCreateNewJob}>
                <span className="createnew-job-button-plus">+</span>
                <span className="createnew-job-button-text">Create New Job</span>
              </button>
            ) : <div />}

            <div className="job-details-menu-container" ref={menuDropdownRef}>
              {showStatusButtons ? (
                <div className="status-buttons-container" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button className="status-cancel-button" onClick={handleCancel}
                    style={{
                      backgroundColor: '#fff', color: '#000', border: '1px solid grey',
                      borderRadius: '4px', padding: '6px 12px', fontSize: '14px',
                      cursor: 'pointer', transition: 'background-color 0.2s'
                    }}>Cancel</button>
                  <button className="status-save-button" onClick={handleSaveChanges}
                    style={{
                      backgroundColor: '#01467d', color: 'white', border: 'none',
                      borderRadius: '4px', padding: '6px 12px', fontSize: '14px',
                      cursor: 'pointer', transition: 'background-color 0.2s'
                    }}>Save Changes</button>
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  <button className="job-details-menu-button" onClick={toggleMenuDropdown}>
                    <span className="menu-dot"></span>
                    <span className="menu-dot"></span>
                    <span className="menu-dot"></span>
                  </button>

                  {showMenuDropdown && (
                    <div style={{
                      position: 'absolute', top: '100%', right: 0, marginTop: '8px',
                      backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 1000,
                      minWidth: '180px', overflow: 'hidden'
                    }}>
                      {/* Export to Excel */}
                      <div
                        onClick={exportToExcel}
                        style={{
                          padding: '10px 16px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: '8px',
                          transition: 'background-color 0.2s', fontSize: '14px', color: '#333',
                          borderBottom: '1px solid #f0f0f0'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                          viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="8" y1="13" x2="16" y2="13" />
                          <line x1="8" y1="17" x2="16" y2="17" />
                          <polyline points="10 9 9 9 8 9" />
                        </svg>
                        Export to Excel
                      </div>

                      {/* Print To Pdf */}
                      <div
                        onClick={printToPDF}
                        style={{
                          padding: '10px 16px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: '8px',
                          transition: 'background-color 0.2s', fontSize: '14px', color: '#333'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                          viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 6 2 18 2 18 9" />
                          <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                          <rect x="6" y="14" width="12" height="8" />
                        </svg>
                        Print To PDF
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <AgGrid
            ref={agGridRef}
            rowData={rowData}
            pinnedBottomRowData={getRowDataWithFooter}
            columnDefs={columnDefs}
            enableSearch={false}
            enableSorting={true}
            enableFilter={true}
            hideExcelExport={true}
            hideSideBar={true}
            height="500px"
            onGridReady={(params) => { gridApiRef.current = params.api; }}
            onFilterChanged={handleFilterChanged}
            gridOptions={{
              defaultColDef: { resizable: true, sortable: true, filter: true },
              suppressRowClickSelection: true,
              rowSelection: 'multiple',
              overlayNoRowsTemplate: `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;
      text-align: center;
    ">

      <div style="font-size: 48px; margin-bottom: 16px; opacity: 0.5;">
        📋
      </div>

      <div style="
        font-size: 16px;
        color: #6c757d;
        font-weight: 500;
      ">
        No records found
      </div>

    </div>
      `,
              tooltipShowDelay: 0,
              tooltipHideDelay: 1000,
              getRowStyle: (params) => {
                if (params.node.rowPinned === 'bottom') {
                  return { backgroundColor: '#f0f8ff', fontWeight: 'bold' };
                }
                return null;
              },
              isRowSelectable: (params) => !params.data || !params.data.isFooter,
              isRowSortable: (params) => !params.data || !params.data.isFooter,
            }}
          />
        </div>
      </div>

    </>
  );
};

export default JobsModule;
